// Building-type factor table — rules/FACTOR_TABLES_MVP.md §2.
//
// Each entry maps a BuildingType to:
//   - the applicable market-price category,
//   - the building-type factor (建筑类型系数),
//   - the property-condition group key (used by property-condition-tables).

import type {
  ApplicablePriceType,
  BuildingType,
  PropertyConditionGroup,
} from "../types/enums.js";

export interface BuildingTypeEntry {
  readonly buildingType: BuildingType;
  readonly applicablePriceType: ApplicablePriceType;
  readonly buildingTypeFactor: number;
  readonly propertyGroup: PropertyConditionGroup;
}

export const BUILDING_TYPE_TABLE: Readonly<Record<BuildingType, BuildingTypeEntry>> = {
  厂房: { buildingType: "厂房", applicablePriceType: "工业", buildingTypeFactor: 1, propertyGroup: "type1" },
  仓库: { buildingType: "仓库", applicablePriceType: "工业", buildingTypeFactor: 1, propertyGroup: "type1" },
  研发用房: { buildingType: "研发用房", applicablePriceType: "新型产业", buildingTypeFactor: 1, propertyGroup: "type2" },
  物流建筑: { buildingType: "物流建筑", applicablePriceType: "新型产业", buildingTypeFactor: 1, propertyGroup: "type2" },
  住宅: { buildingType: "住宅", applicablePriceType: "住宅", buildingTypeFactor: 1, propertyGroup: "type3" },
  宿舍: { buildingType: "宿舍", applicablePriceType: "住宅", buildingTypeFactor: 0.6, propertyGroup: "type3" },
  办公: { buildingType: "办公", applicablePriceType: "办公", buildingTypeFactor: 1, propertyGroup: "type4to9" },
  商务公寓: { buildingType: "商务公寓", applicablePriceType: "办公", buildingTypeFactor: 1, propertyGroup: "type4to9" },
  会议中心: { buildingType: "会议中心", applicablePriceType: "办公", buildingTypeFactor: 0.5, propertyGroup: "type4to9" },
  社会停车场库: { buildingType: "社会停车场库", applicablePriceType: "办公", buildingTypeFactor: 0.1, propertyGroup: "type4to9" },
  邮政网点: { buildingType: "邮政网点", applicablePriceType: "办公", buildingTypeFactor: 0.1, propertyGroup: "type4to9" },
  商业: { buildingType: "商业", applicablePriceType: "商业", buildingTypeFactor: 1, propertyGroup: "type4to9" },
  加油站: { buildingType: "加油站", applicablePriceType: "商业", buildingTypeFactor: 1, propertyGroup: "type4to9" },
  游乐设施: { buildingType: "游乐设施", applicablePriceType: "商业", buildingTypeFactor: 0.4, propertyGroup: "type4to9" },
  加气站: { buildingType: "加气站", applicablePriceType: "商业", buildingTypeFactor: 0.3, propertyGroup: "type4to9" },
  肉菜市场: { buildingType: "肉菜市场", applicablePriceType: "商业", buildingTypeFactor: 0.3, propertyGroup: "type4to9" },
  旅馆业建筑: { buildingType: "旅馆业建筑", applicablePriceType: "酒店", buildingTypeFactor: 1, propertyGroup: "type10" },
  文化设施: { buildingType: "文化设施", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  医疗卫生设施: { buildingType: "医疗卫生设施", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  教育设施: { buildingType: "教育设施", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  体育设施: { buildingType: "体育设施", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  幼儿园托儿所: { buildingType: "幼儿园托儿所", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  文化遗产: { buildingType: "文化遗产", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 0.3, propertyGroup: "type11to15" },
  宗教建筑: { buildingType: "宗教建筑", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 0.3, propertyGroup: "type11to15" },
  社会福利设施: { buildingType: "社会福利设施", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 0.3, propertyGroup: "type11to15" },
  特殊建筑: { buildingType: "特殊建筑", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 0.3, propertyGroup: "type11to15" },
  科研设施: { buildingType: "科研设施", applicablePriceType: "公共管理与服务设施", buildingTypeFactor: 0.1, propertyGroup: "type11to15" },
  公用设施: { buildingType: "公用设施", applicablePriceType: "公用设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  市政设施: { buildingType: "市政设施", applicablePriceType: "公用设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  施工配套设施: { buildingType: "施工配套设施", applicablePriceType: "公用设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  训考场: { buildingType: "训考场", applicablePriceType: "公用设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  殡葬设施: { buildingType: "殡葬设施", applicablePriceType: "公用设施", buildingTypeFactor: 1, propertyGroup: "type11to15" },
  交通设施: { buildingType: "交通设施", applicablePriceType: "公用设施", buildingTypeFactor: 0.3, propertyGroup: "type11to15" },
  综合管廊: { buildingType: "综合管廊", applicablePriceType: "公用设施", buildingTypeFactor: 0.3, propertyGroup: "type11to15" },
};
